const DEFAULT_ORDER = "master_rating";

class StallionTabsManager {
  constructor(root) {
    this.root = root;
    this.tabList = root.querySelector("[data-role='tab-list']");
    this.panelWrapper = root.querySelector("[data-role='tab-panels']");
    this.emptyState = root.querySelector("[data-role='empty-state']");
    this.placeholder = root.querySelector("[data-role='tab-placeholder']");
    this.inputWrapper = root.querySelector("[data-role='input-wrapper']");
    this.searchInput = root.querySelector("[data-role='stallion-input']");
    this.toggleButton = root.querySelector("[data-role='toggle-input']");
    this.spinner = root.querySelector("[data-role='tab-spinner']");
    this.ageFilterBar = root.querySelector(
      "[data-role='comparison-age-filters']"
    );
    this.ratingComparisonWrapper = root.querySelector(
      "[data-role='comparison-chart-rating']"
    );
    this.speedComparisonWrapper = root.querySelector(
      "[data-role='comparison-chart-speed']"
    );
    this.ratingChartElement =
      this.ratingComparisonWrapper?.querySelector(
        "[data-chart-target='comparison-rating']"
      ) || null;
    this.speedChartElement =
      this.speedComparisonWrapper?.querySelector(
        "[data-chart-target='comparison-speed']"
      ) || null;
    this.ratingComparisonChart = null;
    this.speedComparisonChart = null;
    this.visibleAges = new Set(["2", "3", "4+"]);
    this.palette = [
      "#1D4ED8",
      "#DC2626",
      "#059669",
      "#7C3AED",
      "#F59E0B",
      "#0EA5E9",
    ];
    this.fetchUrl = root.dataset.fetchUrl;
    this.autocompleteUrl = root.dataset.autocompleteUrl;
    this.loading = false;

    this.registerTabBehavior();
    this.registerToggleBehavior();
    this.registerComparisonFilterBehavior();
    this.setupAutoComplete();
    this.updateComparisonCharts();
  }

  registerTabBehavior() {
    if (!this.tabList) return;

    this.tabList.addEventListener("click", (event) => {
      const removeTarget = event.target.closest("[data-role='remove-tab']");
      if (removeTarget) {
        event.stopPropagation();
        const tabId = removeTarget.dataset.tabTarget;
        if (tabId) this.removeTab(tabId);
        return;
      }

      const button = event.target.closest("[data-tab-target]");
      if (button && this.tabList.contains(button)) {
        this.activateTab(button.dataset.tabTarget);
      }
    });
  }

  registerToggleBehavior() {
    if (!this.toggleButton || !this.searchInput) return;
    this.toggleButton.addEventListener("click", () => {
      this.searchInput.focus();
      this.searchInput.select();
    });
  }

  registerComparisonFilterBehavior() {
    const toggles = this.root.querySelectorAll(
      "[data-role='comparison-age-toggle']"
    );
    toggles.forEach((toggle) => {
      toggle.addEventListener("change", (event) => {
        const age = event.target.dataset.age;
        if (!age) return;
        if (event.target.checked) {
          this.visibleAges.add(age);
        } else {
          this.visibleAges.delete(age);
        }
        this.updateComparisonCharts();
      });
    });
  }

  setupAutoComplete() {
    if (
      !this.searchInput ||
      typeof window.autoComplete === "undefined" ||
      typeof window.baseAutoCompleteConfig === "undefined"
    ) {
      return;
    }

    const searchId = this.searchInput.id;
    new autoComplete({
      ...window.baseAutoCompleteConfig(searchId),
      selector: `#${searchId}`,
      data: {
        src: async () => {
          const query = this.searchInput.value.trim();
          if (!query) return [];
          try {
            const response = await fetch(
              `${this.autocompleteUrl}?competitor_name=${encodeURIComponent(
                query
              )}`
            );
            if (!response.ok) return [];
            return response.json();
          } catch (error) {
            console.error("Autocomplete fetch failed", error);
            return [];
          }
        },
        key: ["name"],
        cache: false,
      },
      onSelection: (feedback) => {
        const stallionName = feedback.selection.value.name;
        this.searchInput.value = stallionName;
        this.fetchTabData(stallionName);
      },
    });

    window.initializeSearchBehavior?.(searchId);
  }

  collectFilters() {
    const form = document.getElementById("stallion-filters-form");
    if (!form) {
      return { order_by: DEFAULT_ORDER };
    }

    const formData = new FormData(form);
    return {
      order_by: formData.get("order_by") || DEFAULT_ORDER,
      year: formData.getAll("year[]"),
      age: formData.getAll("age[]"),
      sex: formData.getAll("sex[]"),
      country: formData.getAll("country[]"),
      race_type: formData.getAll("race_type[]"),
    };
  }

  async fetchTabData(stallionName) {
    if (!stallionName || this.loading || !this.fetchUrl) return;

    const payload = {
      sire_name: stallionName,
      ...this.collectFilters(),
    };

    this.setLoadingState(true);

    try {
      const response = await fetch(this.fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        this.showError(data.error || "Unable to load stallion results.");
        return;
      }

      this.insertOrUpdateTab(stallionName, data.html);
    } catch (error) {
      console.error("Failed to load stallion tab", error);
      this.showError("Unable to load stallion results.");
    } finally {
      this.setLoadingState(false);
    }
  }

  insertOrUpdateTab(stallionName, html) {
    if (!this.tabList || !this.panelWrapper) return;

    const normalizedName = stallionName.toLowerCase();
    const existingButton = this.tabList.querySelector(
      `[data-stallion-name="${normalizedName}"]`
    );

    if (existingButton) {
      const targetId = existingButton.dataset.tabTarget;
      const panel = this.panelWrapper.querySelector(
        `[data-tab-id="${targetId}"]`
      );
      if (panel) {
        panel.innerHTML = html;
        this.executeScripts(panel);
        this.activateTab(targetId);
        this.updateComparisonCharts();
      }
      return;
    }

    const uniqueId = `stallion-tab-${normalizedName.replace(
      /[^a-z0-9]/gi,
      "-"
    )}-${Date.now()}`;

    const button = document.createElement("button");
    button.type = "button";
    button.dataset.tabTarget = uniqueId;
    button.dataset.stallionName = normalizedName;
    button.className =
      "group px-4 py-2 rounded-full text-sm font-medium border transition-colors tab-trigger bg-white text-gray-700 border-gray-300 hover:border-blue-400 flex items-center gap-2";

    const labelSpan = document.createElement("span");
    labelSpan.textContent = stallionName;

    const removeSpan = document.createElement("span");
    removeSpan.textContent = "×";
    removeSpan.dataset.role = "remove-tab";
    removeSpan.dataset.tabTarget = uniqueId;
    removeSpan.className =
      "opacity-0 group-hover:opacity-100 text-xs leading-none text-gray-500 hover:text-red-600 transition-opacity cursor-pointer rounded-full px-1";

    button.appendChild(labelSpan);
    button.appendChild(removeSpan);

    const hasExistingPanels =
      this.panelWrapper.querySelectorAll("[data-tab-id]").length > 0;
    const panel = document.createElement("div");
    panel.dataset.tabId = uniqueId;
    panel.className = hasExistingPanels ? "hidden" : "";
    panel.innerHTML = html;
    this.executeScripts(panel);

    if (this.placeholder) {
      this.placeholder.remove();
      this.placeholder = null;
    }

    if (this.emptyState) {
      this.emptyState.remove();
      this.emptyState = null;
    }

    this.tabList.appendChild(button);
    this.panelWrapper.appendChild(panel);
    this.activateTab(uniqueId);

    if (this.searchInput) {
      this.searchInput.value = "";
    }

    this.updateComparisonCharts();
  }

  executeScripts(container) {
    if (!container) return;
    container.querySelectorAll("script").forEach((oldScript) => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) =>
        newScript.setAttribute(attr.name, attr.value)
      );
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  updateComparisonCharts() {
    const ratingVisible = this.updateComparisonChartForType({
      wrapper: this.ratingComparisonWrapper,
      element: this.ratingChartElement,
      chartKey: "ratingComparisonChart",
      datasetAttr: "ratingYearSeries",
      yAxisLabel: "Average Master Rating",
    });

    const speedVisible = this.updateComparisonChartForType({
      wrapper: this.speedComparisonWrapper,
      element: this.speedChartElement,
      chartKey: "speedComparisonChart",
      datasetAttr: "speedYearSeries",
      yAxisLabel: "Average Speed Figure",
    });

    const hasComparisonData = ratingVisible || speedVisible;

    if (this.ageFilterBar) {
      this.ageFilterBar.classList.toggle("hidden", !hasComparisonData);
    }
  }

  updateComparisonChartForType({
    wrapper,
    element,
    chartKey,
    datasetAttr,
    yAxisLabel,
  }) {
    if (
      !this.panelWrapper ||
      !wrapper ||
      !element ||
      typeof window.Chartkick === "undefined"
    ) {
      return false;
    }

    const panels = Array.from(
      this.panelWrapper.querySelectorAll("[data-stallion-panel='true']")
    );

    if (panels.length < 2 || this.visibleAges.size === 0) {
      this.hideComparisonChart(wrapper, chartKey);
      return false;
    }

    const datasets = [];

    panels.forEach((panel) => {
      const stallionName = panel.dataset.stallionName || "Stallion";
      const series = this.safeJSON(panel.dataset[datasetAttr]);

      Object.keys(series).forEach((ageLabel) => {
        if (!this.visibleAges.has(ageLabel)) {
          return;
        }
        const yearData = series[ageLabel] || {};
        if (Object.keys(yearData).length === 0) return;

        const orderedData = Object.keys(yearData)
          .sort()
          .reduce((acc, year) => {
            acc[year] = yearData[year];
            return acc;
          }, {});

        datasets.push({
          name: `${stallionName} – Age ${ageLabel}`,
          data: orderedData,
        });
      });
    });

    if (datasets.length === 0) {
      this.hideComparisonChart(wrapper, chartKey);
      return false;
    }

    wrapper.classList.remove("hidden");

    const colors = datasets.map((dataset) => this.colorForSeries(dataset.name));
    const bounds = this.calculateBounds(datasets);
    const options = {
      colors,
      points: true,
      legend: true,
      curve: false,
      library: {
        tension: 0,
        scales: {
          y: {
            title: {
              display: true,
              text: yAxisLabel,
            },
            beginAtZero: false,
            min: bounds?.min,
            max: bounds?.max,
          },
        },
      },
    };

    if (!this[chartKey]) {
      this[chartKey] = new window.Chartkick.LineChart(
        element,
        datasets,
        options
      );
    } else {
      this[chartKey].setOptions(options);
      this[chartKey].updateData(datasets);
    }

    true
  }

  hideComparisonChart(wrapper, chartKey) {
    if (wrapper) {
      wrapper.classList.add("hidden");
    }
    if (this[chartKey] && typeof this[chartKey].destroy === "function") {
      this[chartKey].destroy();
    }
    this[chartKey] = null;
  }

  safeJSON(value) {
    if (!value) return {};
    try {
      const parsed = JSON.parse(value);
      return parsed || {};
    } catch (error) {
      console.error("Failed to parse age series JSON", error);
      return {};
    }
  }

  colorForSeries(seriesName) {
    if (!seriesName) return "#1D4ED8";

    const stallionName = this.extractStallionName(seriesName);
    const hue = Math.abs(this.hashString(stallionName)) % 360;
    const ageLabel = this.extractAgeLabel(seriesName);

    const saturation = 65;
    const lightness = this.lightnessForAge(ageLabel);

    return this.hslToHex(hue, saturation, lightness);
  }

  extractStallionName(seriesName) {
    return seriesName.split("–")[0].trim().toLowerCase() || seriesName;
  }

  extractAgeLabel(seriesName) {
    const shadeMatch = seriesName.match(/Age\s+(\d\+?)/i);
    return shadeMatch ? shadeMatch[1] : null;
  }

  lightnessForAge(ageLabel) {
    // Exaggerate contrast between age groups:
    // 2YO very light, 3YO mid, 4YO+ dark
    if (ageLabel === "2") return 78;
    if (ageLabel === "3") return 52;
    if (ageLabel === "4+") return 28;
    return 52;
  }

  hashString(value) {
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
      hash = value.charCodeAt(i) + ((hash << 5) - hash);
      hash &= hash;
    }
    return hash;
  }

  hslToHex(h, s, l) {
    const saturation = s / 100;
    const lightness = l / 100;

    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lightness - chroma / 2;

    let r = 0;
    let g = 0;
    let b = 0;

    if (h >= 0 && h < 60) {
      r = chroma;
      g = x;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = chroma;
    } else if (h >= 120 && h < 180) {
      g = chroma;
      b = x;
    } else if (h >= 180 && h < 240) {
      g = x;
      b = chroma;
    } else if (h >= 240 && h < 300) {
      r = x;
      b = chroma;
    } else {
      r = chroma;
      b = x;
    }

    const toHex = (value) =>
      Math.round((value + m) * 255)
        .toString(16)
        .padStart(2, "0");

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  calculateBounds(datasets) {
    if (!datasets || datasets.length === 0) return null;

    const values = datasets.flatMap((dataset) =>
      Object.values(dataset.data || {})
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    );

    if (values.length === 0) return null;

    let min = Math.min(...values);
    let max = Math.max(...values);

    if (min === max) {
      const padding = min === 0 ? 5 : Math.abs(min) * 0.1;
      min -= padding;
      max += padding;
    } else {
      const rangePadding = (max - min) * 0.1;
      min -= rangePadding;
      max += rangePadding;
    }

    return {
      min: Math.floor(min),
      max: Math.ceil(max),
    };
  }

  activateTab(tabId) {
    if (!tabId || !this.panelWrapper || !this.tabList) return;

    this.panelWrapper
      .querySelectorAll("[data-tab-id]")
      .forEach((panel) => panel.classList.add("hidden"));
    this.tabList.querySelectorAll("[data-tab-target]").forEach((button) => {
      button.classList.remove("bg-blue-600", "text-white", "border-blue-600");
      button.classList.add(
        "bg-white",
        "text-gray-700",
        "border-gray-300",
        "hover:border-blue-400"
      );
    });

    const activePanel = this.panelWrapper.querySelector(
      `[data-tab-id='${tabId}']`
    );
    const activeButton = this.tabList.querySelector(
      `[data-tab-target='${tabId}']`
    );

    if (activePanel) activePanel.classList.remove("hidden");
    if (activeButton) {
      activeButton.classList.add(
        "bg-blue-600",
        "text-white",
        "border-blue-600"
      );
      activeButton.classList.remove(
        "bg-white",
        "text-gray-700",
        "border-gray-300",
        "hover:border-blue-400"
      );
    }
  }

  removeTab(tabId) {
    if (!tabId || !this.tabList || !this.panelWrapper) return;

    const button = this.tabList.querySelector(
      `[data-tab-target='${tabId}']`
    );
    const panel = this.panelWrapper.querySelector(
      `[data-tab-id='${tabId}']`
    );

    const wasActive = panel && !panel.classList.contains("hidden");

    if (button) button.remove();
    if (panel) panel.remove();

    const remainingPanels = this.panelWrapper.querySelectorAll(
      "[data-tab-id]"
    );

    if (remainingPanels.length === 0) {
      if (!this.placeholder && this.tabList) {
        const span = document.createElement("span");
        span.className = "text-sm text-gray-500";
        span.dataset.role = "tab-placeholder";
        span.textContent = "No stallions selected";
        this.tabList.appendChild(span);
        this.placeholder = span;
      }

      if (!this.emptyState && this.panelWrapper) {
        const empty = document.createElement("div");
        empty.dataset.role = "empty-state";
        empty.className =
          "text-center text-gray-500 py-10 border border-dashed border-gray-300 rounded-lg";
        empty.textContent =
          "Use the filters or the + button to add stallions and compare their runners.";
        this.panelWrapper.appendChild(empty);
        this.emptyState = empty;
      }
    } else if (wasActive) {
      const firstPanel = remainingPanels[0];
      const nextId = firstPanel.dataset.tabId;
      if (nextId) this.activateTab(nextId);
    }

    this.updateComparisonCharts();
  }

  setLoadingState(state) {
    this.loading = state;
    if (!this.toggleButton) return;

    this.toggleButton.classList.toggle("opacity-50", state);
    this.toggleButton.classList.toggle("pointer-events-none", state);
    if (this.searchInput) {
      this.searchInput.disabled = state;
    }
    if (this.spinner) {
      this.spinner.classList.toggle("hidden", !state);
    }
  }

  showError(message) {
    if (window.alert) {
      window.alert(message);
    } else {
      console.error(message);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll("[data-component='stallion-tabs']")
    .forEach((root) => new StallionTabsManager(root));
});
